import { useEffect, useState } from "react";

import { themeClasses as T, C } from "sj-ui-toolkit/src/atlas/utils";

import { useSession } from "../../contexts/SandboxHooks";
import { useStatusOverrides } from "../../helpers/useStatusOverrides";

/**
 * SessionExpiringOverlay - Warning overlay shown when session is about to expire
 * Displays countdown timer and message to the user
 */
const SessionExpiringOverlay = () => {
  const { session } = useSession();
  const statusOverrides = useStatusOverrides();

  const timeoutStatus = statusOverrides?.timeout || session?.timeout || "active";

  // Local countdown timer (1 minute = 60 seconds)
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  // Start countdown when session enters "expiring" state
  useEffect(() => {
    if (timeoutStatus === "expiring") {
      // Initialize countdown at 60 seconds
      setSecondsRemaining(60);

      const countdownInterval = setInterval(() => {
        setSecondsRemaining(prev => {
          const next = prev <= 1 ? 0 : prev - 1;
          return next;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
  }, [timeoutStatus]);

  const minutesRemaining = Math.floor(secondsRemaining / 60);
  const secsRemaining = secondsRemaining % 60;

  if (timeoutStatus !== "expiring") {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className={C("rounded-lg shadow-lg p-8 max-w-md text-center", T.bgElevation2)}>
        <div className="flex justify-center mb-4">
          <i className={C("pi pi-exclamation-triangle", T.textWarning, "text-4xl")} />
        </div>
        <h3 className="text-2xl font-bold mb-2">Sandbox Session Expiring</h3>
        <p className={C("mb-6", T.textSecondary)}>
          Your sandbox session will expire soon due to inactivity. Continue working to reset the
          timer.
        </p>
        <div className={C("text-5xl font-bold font-mono mb-6", T.textWarning)}>
          {String(minutesRemaining).padStart(2, "0")}:{String(secsRemaining).padStart(2, "0")}
        </div>
        <p className={C("text-sm", T.textPrimaryDarker)}>
          Click anywhere to dismiss this warning and maintain your session.
        </p>
      </div>
    </div>
  );
};

export default SessionExpiringOverlay;
